import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Empty, Modal } from "@/components/ui-bits";
import { TagInput } from "@/components/TagInput";

const blank = { name: "", description: "", items: [{ name: "", prescription: "" }], injuries: [] };

const PlanForm = ({ plan, onClose, onSaved }) => {
  const [f, setF] = useState(plan ? { name: plan.name, description: plan.description || "", items: plan.items.map((i) => ({ name: i.name, prescription: i.prescription || "" })), injuries: plan.injuries || [] } : blank);
  const [busy, setBusy] = useState(false);
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const save = async (e) => {
    e.preventDefault();
    const items = f.items.filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), prescription: i.prescription.trim() || null }));
    if (!items.length) return toast.error("Add at least one item");
    setBusy(true);
    try {
      const body = { name: f.name.trim(), description: f.description.trim() || null, items, injuries: f.injuries };
      if (plan) await api.put(`/recovery-plans/${plan.id}`, body); else await api.post("/recovery-plans", body);
      toast.success(plan ? "Recovery plan updated" : "Recovery plan added"); onSaved(); onClose();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Modal title={plan ? "EDIT RECOVERY PLAN" : "NEW RECOVERY PLAN"} sub="Clients tick these off like a normal training day" onClose={onClose} testId="recovery-plan-modal">
      <form onSubmit={save}>
        <label>Name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Knee-friendly recovery" data-testid="recovery-plan-name-input" />
        <label style={{ marginTop: 8, display: "block" }}>Description</label><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="When to use it, what to avoid…" data-testid="recovery-plan-desc-input" />
        <label style={{ marginTop: 8, display: "block" }}>Suits these injuries · pre-selected for tagged clients</label>
        <TagInput value={f.injuries} onChange={(v) => setF({ ...f, injuries: v })} testId="recovery-plan-injuries" />
        <label style={{ marginTop: 8, display: "block" }}>Items</label>
        {f.items.map((it, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6, marginBottom: 6 }} data-testid={`recovery-plan-item-${i}`}>
            <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Exercise / activity" data-testid={`recovery-plan-item-name-${i}`} />
            <input value={it.prescription} onChange={(e) => setItem(i, "prescription", e.target.value)} placeholder="2 × 45s each side" data-testid={`recovery-plan-item-rx-${i}`} />
            <button type="button" className="btn sm g" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} disabled={f.items.length === 1} aria-label="Remove item" data-testid={`recovery-plan-item-remove-${i}`}><Trash2 size={13} /></button>
          </div>))}
        {f.items.length < 12 && <button type="button" className="linkbtn" onClick={() => setF({ ...f, items: [...f.items, { name: "", prescription: "" }] })} data-testid="recovery-plan-add-item"><Plus size={12} /> Add item</button>}
        <div className="mfoot"><button type="button" className="btn g" onClick={onClose}>Cancel</button><button className="btn" disabled={busy} data-testid="recovery-plan-save">{busy ? "Saving…" : "Save plan"}</button></div>
      </form>
    </Modal>
  );
};

export const RecoveryPlans = () => {
  const [plans, setPlans] = useState(null);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => api.get("/recovery-plans").then((r) => setPlans(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return; try { await api.delete(`/recovery-plans/${p.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!plans) return null;
  return (
    <div className="panel" data-testid="recovery-plans">
      <div className="ph"><h3>Recovery plans · attached to a session when you apply a deload</h3>
        <button className="btn sm" onClick={() => setModal("new")} data-testid="add-recovery-plan-button">+ New plan</button></div>
      <p className="ss" style={{ marginTop: 0 }}>The built-in "Recovery day" is the default. Add variants for specific issues — knee, shoulder, lower back — and pick one when applying a deload.</p>
      {plans.length === 0 && <Empty>No plans yet.</Empty>}
      {plans.map((p) => (
        <div className="excard" key={p.id} data-testid={`recovery-plan-${p.id}`}>
          <div className="ph" style={{ marginBottom: 4 }}>
            <div className="st">{p.name}{p.builtin && <span className="chip gray" style={{ marginLeft: 8, fontSize: 10 }}>built-in</span>}<span className="ss"> · {p.items.length} items</span>{(p.injuries || []).map((t) => <span className="injury" key={t} style={{ marginLeft: 6 }}>{t}</span>)}</div>
            <div className="actions">
              <button className="btn sm g" onClick={() => setModal(p)} data-testid={`edit-recovery-plan-${p.id}`}>Edit</button>
              {!p.builtin && <button className="btn sm danger" onClick={() => remove(p)} data-testid={`delete-recovery-plan-${p.id}`}>Delete</button>}
            </div>
          </div>
          {p.description && <div className="ss" style={{ marginBottom: 4 }}>{p.description}</div>}
          <div className="ss">{p.items.map((i) => `${i.name}${i.prescription ? ` (${i.prescription})` : ""}`).join(" · ")}</div>
        </div>
      ))}
      {modal && <PlanForm plan={modal === "new" ? null : modal} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
};
